import React, { useState } from 'react';
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBListGroup,
  MDBListGroupItem,
  MDBBtn,
  MDBInput,
  MDBIcon,
  MDBBadge,
  MDBSpinner,
  MDBTypography
} from 'mdb-react-ui-kit';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useEventProducts } from './hooks/useEventProducts';
import { transformMoney } from '../../../utils/money';
import { getLocalizedText } from '../../../utils/localizedText';
import TimeRangePriceModal, { TimeRangePriceButton } from '../../products/TimeRangePriceModal';

const EquipmentTab = ({ eventId }) => {
  const { t } = useTranslation();
  const {
    isLoading,
    eventProducts,
    availableProducts,
    addProductToEvent,
    removeProductFromEvent,
    updateEventProduct,
    reorderProducts
  } = useEventProducts(eventId, 'equipment');

  const [productPrices, setProductPrices] = useState({});
  const [productStocks, setProductStocks] = useState({});
  const [selectedEventProduct, setSelectedEventProduct] = useState(null);
  const [timeRangePriceModalOpen, setTimeRangePriceModalOpen] = useState(false);

  // Group products by subType for better organization
  const productGroups = {
    furniture: { label: t('Furniture'), icon: 'couch', products: [] },
    electrical: { label: t('Electrical'), icon: 'plug', products: [] },
    compressed_air: { label: t('Compressed Air'), icon: 'wind', products: [] },
    sanitary: { label: t('Sanitary'), icon: 'toilet', products: [] },
    internet_cable: { label: t('Internet - Cable'), icon: 'ethernet', products: [] },
    internet_wifi: { label: t('Internet - WiFi'), icon: 'wifi', products: [] },
    display_print: { label: t('Display & Print'), icon: 'print', products: [] },
    carpet: { label: t('Carpet'), icon: 'rug', products: [] },
    other: { label: t('Other'), icon: 'box', products: [] }
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <MDBSpinner />
        <div className="mt-2">{t('Loading')}</div>
      </div>
    );
  }

  // Group available products by subType
  const availableProductsFiltered = availableProducts.filter(
    product => !eventProducts.some(ep => ep.product.id === product.id)
  );

  availableProductsFiltered.forEach(product => {
    const group = productGroups[product.subType] || productGroups.other;
    group.products.push(product);
  });

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(eventProducts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    reorderProducts(items);
  };

  const handleAddProduct = (productId) => {
    const price = productPrices[productId] || availableProducts.find(p => p.id === productId)?.price;
    const stock = productStocks[productId] || availableProducts.find(p => p.id === productId)?.stock;
    addProductToEvent(productId, price, stock);
  };

  const handlePriceChange = (eventProductId, newPrice) => {
    updateEventProduct(eventProductId, { price: parseFloat(newPrice) });
  };

  const handleStockChange = (eventProductId, newStock) => {
    updateEventProduct(eventProductId, { stock: parseInt(newStock) });
  };

  const handleUpdateEventProductWithTimeRange = (eventProductId, price, stock, timeRangePrices) => {
    updateEventProduct(eventProductId, { price, stock, timeRangePrices });
  };

  return (
    <>
      <MDBRow className="mb-4">
      <MDBCol size="12" lg="6">
        <MDBCard className="h-100">
          <MDBCardBody>
            <MDBCardTitle>{t('Available Products')}</MDBCardTitle>
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {Object.entries(productGroups).map(([key, group]) => {
                if (group.products.length === 0) return null;

                return (
                  <div key={key} className="mb-4">
                    <MDBTypography tag="h6" className="text-muted mb-2">
                      <MDBIcon fas icon={group.icon} className="me-2" />
                      {group.label}
                    </MDBTypography>
                    <MDBListGroup>
                      {group.products.map((product) => (
                        <MDBListGroupItem key={product.id} className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center flex-grow-1">
                            {product.image?.main?.fileName ? (
                              <img
                                src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/${product.image.main.fileName}`}
                                style={{ height: '40px', width: '40px', objectFit: 'contain' }}
                                alt=""
                                className="me-2"
                              />
                            ) : (
                              <MDBIcon
                                fas
                                icon={product.icon || group.icon}
                                size="2x"
                                className="me-2"
                                style={{ width: '40px', textAlign: 'center' }}
                              />
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-bold">{getLocalizedText(product.name)}</div>
                              <div className="text-muted small mb-2">{product.sku}</div>
                              <div className="d-flex align-items-center">
                                <MDBInput
                                  type="number"
                                  label={t("Price")}
                                  defaultValue={product.price}
                                  onChange={(e) => setProductPrices({ ...productPrices, [product.id]: e.target.value })}
                                >
                                  <i className="fas fa-euro-sign trailing"></i>
                                </MDBInput>
                                <div style={{ width: '40px' }}></div> {/* Spacer for missing TimeRangeButton */}
                                <MDBInput
                                  type="number"
                                  label={t("Stock")}
                                  defaultValue={product.stock}
                                  onChange={(e) => setProductStocks({ ...productStocks, [product.id]: e.target.value })}
                                />
                                <MDBBtn
                                  color="primary"
                                  className="ms-2"
                                  style={{width: '250px'}}
                                  size="sm"
                                  outline
                                  onClick={() => handleAddProduct(product.id)}
                                >
                                  {t('Add')}
                                </MDBBtn>
                              </div>
                            </div>
                          </div>
                        </MDBListGroupItem>
                      ))}
                    </MDBListGroup>
                  </div>
                );
              })}
            </div>
          </MDBCardBody>
        </MDBCard>
      </MDBCol>

      <MDBCol size="12" lg="6">
        <MDBCard className="h-100">
          <MDBCardBody>
            <MDBCardTitle>
              {t('Equipment Products')}
              <MDBBadge className="ms-2" color="primary">{eventProducts.length}</MDBBadge>
            </MDBCardTitle>
            <p className="text-muted small">{t('Drag to reorder')}</p>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="equipmentProducts">
                {(provided) => (
                  <MDBListGroup
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ maxHeight: '600px', overflowY: 'auto' }}
                  >
                    {eventProducts.map((eventProduct, index) => {
                      const groupIcon = productGroups[eventProduct.product.subType]?.icon || 'box';

                      return (
                        <Draggable
                          key={eventProduct.id}
                          draggableId={eventProduct.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <MDBListGroupItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`d-flex justify-content-between align-items-center ${
                                snapshot.isDragging ? 'shadow-3' : ''
                              }`}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white'
                              }}
                            >
                              <div className="d-flex align-items-center flex-grow-1">
                                <div
                                  {...provided.dragHandleProps}
                                  className="me-2"
                                  style={{ cursor: 'grab' }}
                                >
                                  <MDBIcon fas icon="grip-vertical" />
                                </div>
                                {eventProduct.product.image?.main?.fileName ? (
                                  <img
                                    src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/${eventProduct.product.image.main.fileName}`}
                                    style={{ height: '40px', width: '40px', objectFit: 'contain' }}
                                    alt=""
                                    className="me-2"
                                  />
                                ) : (
                                  <MDBIcon
                                    fas
                                    icon={eventProduct.product.icon || groupIcon}
                                    size="2x"
                                    className="me-2"
                                    style={{ width: '40px', textAlign: 'center' }}
                                  />
                                )}
                                <div className="flex-grow-1">
                                  <div className="fw-bold">{getLocalizedText(eventProduct.product.name)}</div>
                                  <div className="text-muted small mb-2">
                                    {eventProduct.product.sku}
                                    {eventProduct.product.subType && (
                                      <MDBBadge className="ms-2" color="light" pill>
                                        {productGroups[eventProduct.product.subType]?.label || eventProduct.product.subType}
                                      </MDBBadge>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <MDBInput
                                      type="number"
                                      label={t("Price")}
                                      value={eventProduct.price || ''}
                                      onChange={(e) => handlePriceChange(eventProduct.id, e.target.value)}
                                    >
                                      <i className="fas fa-euro-sign trailing"></i>
                                    </MDBInput>
                                    <TimeRangePriceButton
                                      hasTimeRangePrices={eventProduct.timeRangePrices?.length > 0}
                                      onClick={() => {
                                        setSelectedEventProduct(eventProduct);
                                        setTimeRangePriceModalOpen(true);
                                      }}
                                    />
                                    <MDBInput
                                      type="number"
                                      label={t("Stock")}
                                      value={eventProduct.stock || ''}
                                      onChange={(e) => handleStockChange(eventProduct.id, e.target.value)}
                                    />
                                    <MDBBtn
                                      onClick={() => removeProductFromEvent(eventProduct.id)}
                                      color="danger"
                                      className="ms-2"
                                      style={{width: '250px'}}
                                      size="sm"
                                      outline
                                    >
                                      {t("Remove")}
                                    </MDBBtn>
                                  </div>
                                </div>
                              </div>
                            </MDBListGroupItem>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </MDBListGroup>
                )}
              </Droppable>
            </DragDropContext>
          </MDBCardBody>
        </MDBCard>
      </MDBCol>
    </MDBRow>

    {selectedEventProduct && (
      <TimeRangePriceModal
        isOpen={timeRangePriceModalOpen}
        onClose={() => {
          setTimeRangePriceModalOpen(false);
          setSelectedEventProduct(null);
        }}
        eventProduct={selectedEventProduct}
        onUpdateEventProduct={handleUpdateEventProductWithTimeRange}
      />
    )}
    </>
  );
};

export default EquipmentTab;